import React, { useEffect, useRef, useMemo, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Box, Tabs, Tab } from '@material-ui/core';
import StepRouter from '../../integration-wizard/embedded/steps/ConfigStep/StepRouter';
import IntegrationWizardContext from '../../integration-wizard/embedded/wizard/IntegrationWizardContext';
import { useIntegrationConfig } from '../../integration-wizard/embedded/hooks/useIntegrationConfig';
import SearchBoxIntegrationSettings from './SearchBoxIntegrationSettings';
import styles from './NestedTabs.module.scss';
import IntlMessages from 'util/IntlMessages';
import { useLocalizedMessage } from 'util/useLocalizedMessage';

/**
 * SearchBox Settings Tab - Configuration for search box component
 * Uses embedded wizard steps from Integration Wizard
 */
const SearchBoxSettings = ({ client, shopId, onRegisterRef, onHasChanges }) => {
  const t = useLocalizedMessage();
  const currentShop = useSelector((s) => s.app.currentShop);
  const effectiveShopId = shopId || currentShop;
  const [activeSubTab, setActiveSubTab] = useState('interface');

  const stepSaveRef = useRef(null);
  const stepReloadRef = useRef(null);

  const {
    config,
    loading,
    error,
    loadConfig,
    saveConfig,
    updateViewIntegration,
  } = useIntegrationConfig(client, effectiveShopId);

  useEffect(() => {
    if (onRegisterRef) {
      onRegisterRef({
        save: async () => {
          if (stepSaveRef.current) {
            await stepSaveRef.current();
          } else if (config) {
            await saveConfig(config);
          }
        },
        reload: async () => {
          if (activeSubTab === 'interface' && stepReloadRef.current) {
            await stepReloadRef.current('preview');
            return;
          }
          await loadConfig('preview');
        },
      });
    }
  }, [onRegisterRef, saveConfig, config, loadConfig, activeSubTab]);

  const handleStepUpdate = useCallback(() => {
    if (onHasChanges) onHasChanges(true);
  }, [onHasChanges]);

  const handleRegisterSave = useCallback((saveFn) => {
    stepSaveRef.current = saveFn;
  }, []);

  const handleRegisterReload = useCallback((reloadFn) => {
    stepReloadRef.current = reloadFn;
  }, []);

  const handleReady = useCallback(() => {}, []);

  const contextValue = useMemo(() => ({
    shopId: effectiveShopId,
    client,
    config,
    saveConfig,
    updateViewIntegration,
    isExpanded: false,
    onExpandToggle: null,
    onUpdate: handleStepUpdate,
    onReady: handleReady,
    onRegisterSave: handleRegisterSave,
    onRegisterReload: handleRegisterReload,
  }), [
    effectiveShopId,
    client,
    config,
    saveConfig,
    updateViewIntegration,
    handleStepUpdate,
    handleReady,
    handleRegisterSave,
    handleRegisterReload,
  ]);

  if (loading) {
    return <Box p={4}><IntlMessages id="ui.searchSettings.searchBoxSettings.loadingConfiguration" /></Box>;
  }

  if (error) {
    return (
      <Box p={4} color="error.main">
        <IntlMessages id="ui.searchSettings.searchBoxSettings.errorLoadingConfiguration" />
        {' '}
        {error}
      </Box>
    );
  }

  if (!config) {
    return <Box p={4}><IntlMessages id="ui.searchSettings.searchBoxSettings.noConfigurationFound" /></Box>;
  }

  const availableViews = config?.search?.views || {};
  const searchView = availableViews.searchBox ? 'searchBox' : (availableViews.dropdown ? 'dropdown' : 'searchBox');
  const interfaceStepId = searchView === 'dropdown' ? 'classic-config' : 'searchbox-config';
  const interfaceTitle = t(searchView === 'dropdown'
    ? 'search.settings.searchBox.dropdownSettingsTitle'
    : 'search.settings.searchBox.settingsTitle');
  const integrationTitle = t(searchView === 'dropdown'
    ? 'search.settings.searchBox.dropdownIntegrationTitle'
    : 'search.settings.searchBox.integrationTitle');

  return (
    <IntegrationWizardContext.Provider value={contextValue}>
      <Box className={styles.root}>
        {/* Nested Tabs */}
        <Box className={styles.nestedTabs}>
          <Tabs
            value={activeSubTab}
            onChange={(e, newTab) => setActiveSubTab(newTab)}
            textColor="primary"
            TabIndicatorProps={{ style: { display: 'none' } }}
          >
            <Tab className={styles.tab} value="interface" label={<IntlMessages id="ui.searchSettings.searchBoxSettings.interface" />} />
            <Tab className={styles.tab} value="integration" label={<IntlMessages id="ui.searchSettings.searchBoxSettings.integration" />} />
          </Tabs>
        </Box>

        {/* Tab Content */}
        <Box className={styles.tabContent}>
          {activeSubTab === 'interface' && (
            <StepRouter
              stepId={interfaceStepId}
              title={interfaceTitle}
              description={<IntlMessages id="ui.searchSettings.searchBoxSettings.configureSearchBoxBehaviorAndAppearance" />}
              data={{}}
            />
          )}

          {activeSubTab === 'integration' && (
            <SearchBoxIntegrationSettings
              title={integrationTitle}
              view={searchView}
              config={config}
              saveConfig={saveConfig}
              updateViewIntegration={updateViewIntegration}
              onReady={handleReady}
              onRegisterSave={handleRegisterSave}
              onUpdate={handleStepUpdate}
            />
          )}
        </Box>
      </Box>
    </IntegrationWizardContext.Provider>
  );
};

SearchBoxSettings.propTypes = {
  client: PropTypes.shape({
    loadSearchExperienceConfig: PropTypes.func.isRequired,
    saveSearchExperienceConfig: PropTypes.func.isRequired,
  }).isRequired,
  shopId: PropTypes.string,
  onRegisterRef: PropTypes.func,
  onHasChanges: PropTypes.func,
};

SearchBoxSettings.defaultProps = {
  shopId: null,
  onRegisterRef: null,
  onHasChanges: null,
};

export default SearchBoxSettings;
